/**
 * Search Popup Product Ordering with Safari Fixes
 * Ensures products appear first in search results
 */

(function () {
  'use strict';

  // The autocomplete container Globo/jQuery UI renders (id can change, so use classes)
  const CONTAINER_SELECTOR = '.ui-autocomplete.glFilter-search-popup.ui-menu';
  const PRODUCTS_LI_CLASS = 'gf-search-products';

  /** @type {Element | null} */
  let container = null;
  /** @type {MutationObserver | null} */
  let containerObserver = null;
  /** @type {number | null} */
  let debounceTimer = null;

  function findContainer() {
    return document.querySelector(CONTAINER_SELECTOR) || document.querySelector('.glFilter-search-popup.ui-menu');
  }

  function moveProductsFirst() {
    if (!container) return;

    // Only reorder top-level items in the popup
    const products = container.querySelector(`:scope > li.${PRODUCTS_LI_CLASS}`);
    if (!products || !container.firstElementChild) return;

    // Guard: only move if not already first (prevents recursive mutation loops)
    if (container.firstElementChild !== products) {
      container.insertBefore(products, container.firstElementChild);
    }

    // Safari fixes: Apply webkit transforms for better rendering
    if (container instanceof HTMLElement && container.style) {
      container.style.webkitTransform = 'translateZ(0)';
      container.style.transform = 'translateZ(0)';
      container.style.webkitBackfaceVisibility = 'hidden';
      container.style.backfaceVisibility = 'hidden';
    }
  }

  function observeContainer() {
    if (containerObserver) containerObserver.disconnect();
    if (!container) return;

    containerObserver = new MutationObserver((mutations) => {
      // Debounce to avoid thrashing on rapid updates
      for (const m of mutations) {
        if (m.type === 'childList') {
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = setTimeout(moveProductsFirst, 60);
          break;
        }
      }
    });

    containerObserver.observe(container, { childList: true, subtree: true });
  }

  function ensureSetup() {
    const current = findContainer();
    if (current && current !== container) {
      container = current;
      moveProductsFirst();
      observeContainer();
    }
  }

  // Initial setup + keep-alive (handles jQuery UI re-render replacing the node)
  ensureSetup();
  const poller = setInterval(ensureSetup, 400); // low-cost polling; stop if not needed

  // Mobile search product ordering with smooth transitions
  let mobileReorderTimeout = null;
  let lastReorderTime = 0;

  function orderMobileProducts() {
    // Debounce to prevent excessive reordering
    const now = Date.now();
    if (now - lastReorderTime < 500) {
      if (mobileReorderTimeout) clearTimeout(mobileReorderTimeout);
      mobileReorderTimeout = setTimeout(() => {
        orderMobileProducts();
      }, 300);
      return;
    }
    lastReorderTime = now;
    // Try multiple mobile containers
    const mobileContainers = [
      document.querySelector('#glFilter-search-popup'),
      document.querySelector('.gl-fullscreen-results__content'),
      document.querySelector('.gl-fullscreen-initial-content'),
      document.querySelector('.predictive-search-dropdown'),
      document.querySelector('#predictive-search-results'),
      document.querySelector('.gl-filter-search-fullscreen'),
    ].filter(Boolean);

    mobileContainers.forEach((container) => {
      if (!container) return;

      // Add smooth transition styles
      const style = document.createElement('style');
      style.textContent = `
        .gl-row, .gl-col-left, .gl-col-right, .gl-list__group, .gl-fullscreen-top-clicked {
          transition: all 0.3s ease-in-out !important;
        }
        .gl-row.reordering, .gl-col-left.reordering, .gl-col-right.reordering, 
        .gl-list__group.reordering, .gl-fullscreen-top-clicked.reordering {
          transition: all 0.3s ease-in-out !important;
        }
      `;
      if (!document.querySelector('#mobile-smooth-transitions')) {
        style.id = 'mobile-smooth-transitions';
        document.head.appendChild(style);
      }

      // Method 1: Try to find and reorder the gl-row structure
      const rowContainer = container.querySelector('.gl-row');
      if (rowContainer) {
        const leftColumn = rowContainer.querySelector('.gl-col-left');
        const rightColumn = rowContainer.querySelector('.gl-col-right');

        if (leftColumn && rightColumn) {
          // Add reordering class for smooth transition
          rowContainer.classList.add('reordering');
          rightColumn.classList.add('reordering');
          leftColumn.classList.add('reordering');

          // Move right column (products) to first position
          if (rowContainer.firstElementChild !== rightColumn) {
            rowContainer.insertBefore(rightColumn, rowContainer.firstElementChild);
          }

          // Remove reordering class after transition
          setTimeout(() => {
            rowContainer.classList.remove('reordering');
            rightColumn.classList.remove('reordering');
            leftColumn.classList.remove('reordering');
          }, 300);
        }
      }

      // Method 2: Try to reorder individual sections within the container
      const productsSection = container.querySelector('.gl-fullscreen-top-clicked');
      const findGroupByTitle = (text) => {
        const title = Array.from(container.querySelectorAll('.gl-list__title')).find(
          (el) => el.textContent && el.textContent.trim().includes(text),
        );
        return title ? title.closest('.gl-list__group') : null;
      };
      const informationSection = findGroupByTitle('Information');
      const blogSection = findGroupByTitle('Blog Posts');

      if (productsSection && (informationSection || blogSection)) {
        // Add smooth transition
        productsSection.classList.add('reordering');
        if (informationSection) informationSection.classList.add('reordering');
        if (blogSection) blogSection.classList.add('reordering');

        // Move products section to first position
        const parent = productsSection.parentNode;
        if (parent && parent.firstElementChild !== productsSection) {
          parent.insertBefore(productsSection, parent.firstElementChild);
        }

        // Remove reordering class after transition
        setTimeout(() => {
          productsSection.classList.remove('reordering');
          if (informationSection) informationSection.classList.remove('reordering');
          if (blogSection) blogSection.classList.remove('reordering');
        }, 300);
      }

      // Method 3: Try to reorder based on gl-list__group structure
      const allGroups = container.querySelectorAll('.gl-list__group');
      const productsGroup = Array.from(allGroups).find((group) =>
        group.querySelector('.gl-list__title')?.textContent?.includes('Products'),
      );

      if (productsGroup && allGroups.length > 1) {
        // Add smooth transition to all groups
        allGroups.forEach((group) => group.classList.add('reordering'));

        const parent = productsGroup.parentNode;
        if (parent && parent.firstElementChild !== productsGroup) {
          parent.insertBefore(productsGroup, parent.firstElementChild);
        }

        // Remove reordering class after transition
        setTimeout(() => {
          allGroups.forEach((group) => group.classList.remove('reordering'));
        }, 300);
      }

      // Method 4: Try to reorder gl-col-right to appear before gl-col-left
      const leftCol = container.querySelector('.gl-col-left');
      const rightCol = container.querySelector('.gl-col-right');

      if (leftCol && rightCol && leftCol.parentNode) {
        // Add smooth transition
        leftCol.classList.add('reordering');
        rightCol.classList.add('reordering');

        const parent = leftCol.parentNode;
        if (parent.firstElementChild !== rightCol) {
          parent.insertBefore(rightCol, parent.firstElementChild);
        }

        // Remove reordering class after transition
        setTimeout(() => {
          leftCol.classList.remove('reordering');
          rightCol.classList.remove('reordering');
        }, 300);
      }
    });
  }

  // Mobile search observer
  const mobileObserver = new MutationObserver((mutations) => {
    let shouldReorder = false;
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE && node instanceof Element) {
            if (
              node.classList &&
              (node.classList.contains('gl-card') ||
                node.classList.contains('gl-fullscreen-top-clicked') ||
                node.classList.contains('gl-col-right') ||
                node.querySelector('.gl-card, .gl-fullscreen-top-clicked'))
            ) {
              shouldReorder = true;
            }
          }
        });
      }
    });

    if (shouldReorder) {
      setTimeout(orderMobileProducts, 100);
    }
  });

  // Start observing mobile search containers
  const mobileContainers = [
    document.querySelector('#glFilter-search-popup'),
    document.querySelector('.gl-fullscreen-results__content'),
    document.querySelector('.gl-fullscreen-initial-content'),
    document.querySelector('.predictive-search-dropdown'),
    document.querySelector('#predictive-search-results'),
  ].filter(Boolean);

  mobileContainers.forEach((container) => {
    if (container) {
      mobileObserver.observe(container, {
        childList: true,
        subtree: true,
      });
    }
  });

  // Also run on input events for mobile search
  document.addEventListener(
    'input',
    (e) => {
      if (
        e.target &&
        e.target instanceof Element &&
        e.target.matches('input[type="search"], input[name="q"], #cmdk-input')
      ) {
        setTimeout(orderMobileProducts, 200);
      }
    },
    { passive: true },
  );

  // Reduced mobile-specific polling for smoother experience
  const mobilePoller = setInterval(() => {
    const mobileSearchActive = document.querySelector(
      '#glFilter-search-popup, .gl-fullscreen-initial-content, .gl-filter-search-fullscreen',
    );
    if (mobileSearchActive) {
      orderMobileProducts();
    }
  }, 1000); // Reduced from 500ms to 1000ms

  // Run mobile ordering on page load with staggered delays
  setTimeout(orderMobileProducts, 500);
  setTimeout(orderMobileProducts, 1500);

  // Nudge after user types in search fields (only relevant inputs)
  document.addEventListener(
    'input',
    (e) => {
      if (
        e.target &&
        e.target instanceof Element &&
        e.target.matches('input[type="search"], input.ui-autocomplete-input')
      ) {
        // Allow Globo to render, then reorder
        setTimeout(moveProductsFirst, 80);
      }
    },
    { passive: true },
  );

  // Optional: stop polling once we've successfully attached and kept stable for a while
  let stableCount = 0;
  const stabilityCheck = setInterval(() => {
    if (container && document.body.contains(container)) {
      stableCount++;
      if (stableCount >= 10) {
        clearInterval(stabilityCheck);
        clearInterval(poller);
      }
    } else {
      stableCount = 0;
    }
  }, 500);

  // Focus is handled by custom-search.liquid (dialog:open + openSearchModal).
  // Only remove scroll-lock when search is open so inputs remain focusable.
  document.addEventListener(
    'dialog:open',
    function (e) {
      const el = e.target && e.target instanceof Element ? e.target : null;
      if (el && el.id === 'search-modal') {
        document.documentElement.classList.remove('scroll-lock');
        document.documentElement.removeAttribute('scroll-lock');
        document.body.classList.remove('scroll-lock');
        const dialogEl = document.querySelector('dialog.search-modal__content.dialog-modal');
        if (dialogEl) {
          dialogEl.classList.remove('scroll-lock');
          dialogEl.removeAttribute('scroll-lock');
        }
      }
    },
    true,
  );
})();
